import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  MessageCircle, 
  User, 
  Phone, 
  Calendar, 
  RefreshCw,
  Search,
  Clock,
  CheckCheck,
  AlertCircle,
  Store,
  ExternalLink,
  Send,
  Brain,
  Sparkles,
  ShoppingCart,
  Loader2
} from 'lucide-react';

const WhatsAppMessagesPage = () => {
  const { API_URL, getAuthHeader } = useAuth();
  const { t } = useLanguage();
  
  const [messages, setMessages] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  
  // Manual test message state
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [creatingOrder, setCreatingOrder] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [messagesRes, customersRes] = await Promise.all([
        axios.get(`${API_URL}/api/whatsapp/messages`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/customers`, { headers: getAuthHeader() })
      ]);
      setMessages(messagesRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('حدث خطأ في تحميل الرسائل');
    } finally {
      setLoading(false);
    }
  };

  // Send WhatsApp reply
  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedConversation) return;
    
    setSendingReply(true);
    try {
      await axios.post(
        `${API_URL}/api/whatsapp/send`,
        { to: selectedConversation.phone, body: replyText },
        { headers: getAuthHeader() }
      );
      toast.success('تم إرسال الرسالة');
      setReplyText('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إرسال الرسالة - تأكد من إعداد WhatsApp API');
    } finally {
      setSendingReply(false);
    }
  };

  // Analyze message with AI
  const handleAnalyzeMessage = async (messageText, messageId) => {
    setAnalyzing(true);
    setAnalysisResult(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/whatsapp/analyze-message`,
        { message_text: messageText, message_id: messageId },
        { headers: getAuthHeader() }
      );
      setAnalysisResult(res.data.analysis);
      toast.success('تم تحليل الرسالة');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ في التحليل');
    } finally {
      setAnalyzing(false);
    }
  };

  // Test message manually
  const handleTestMessage = async () => {
    if (!testPhone || !testMessage) {
      toast.error('يرجى إدخال رقم الهاتف والرسالة');
      return;
    }
    
    setTestLoading(true);
    setTestResult(null);
    
    try {
      const res = await axios.post(
        `${API_URL}/api/whatsapp/test-message`,
        { 
          phone: testPhone, 
          message: testMessage 
        },
        { headers: getAuthHeader() }
      );
      setTestResult(res.data);
      toast.success('تم تحليل الرسالة بنجاح');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ في التحليل');
    } finally {
      setTestLoading(false);
    }
  };

  // Create order from test result
  const handleCreateOrderFromTest = async () => {
    if (!testResult?.parsed_order || !testResult?.customer) {
      toast.error('لا يوجد طلب لإنشائه');
      return;
    }
    
    setCreatingOrder(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/whatsapp/create-order-from-message`,
        { 
          customer_id: testResult.customer.id,
          items: testResult.parsed_order.items,
          date: new Date().toISOString().split('T')[0]
        },
        { headers: getAuthHeader() }
      );
      toast.success(`تم إنشاء الطلب رقم: ${res.data.order_id}`);
      setTestDialogOpen(false);
      setTestResult(null);
      setTestPhone('');
      setTestMessage('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ في إنشاء الطلب');
    } finally {
      setCreatingOrder(false);
    }
  };

  // Group messages by phone number
  const getConversations = () => {
    const grouped = {};
    messages.forEach(msg => {
      const phone = msg.from_phone;
      if (!grouped[phone]) {
        grouped[phone] = {
          phone,
          messages: [],
          customer: customers.find(c => c.whatsapp_phone === phone || c.phone === phone),
          lastMessage: null,
          unreadCount: 0
        };
      }
      grouped[phone].messages.push(msg);
    });
    
    // Sort by last message date and set last message
    Object.values(grouped).forEach(conv => {
      conv.messages.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      conv.lastMessage = conv.messages[0];
    });
    
    return Object.values(grouped).sort((a, b) => 
      new Date(b.lastMessage?.created_at) - new Date(a.lastMessage?.created_at)
    );
  };

  const conversations = getConversations();

  const filteredConversations = conversations.filter(conv => {
    const searchLower = searchQuery.toLowerCase();
    return (
      conv.phone.includes(searchQuery) ||
      conv.customer?.name?.toLowerCase().includes(searchLower) ||
      conv.customer?.shop_name?.toLowerCase().includes(searchLower) ||
      conv.lastMessage?.message_text?.toLowerCase().includes(searchLower)
    );
  });

  const openConversation = (conv) => {
    setSelectedConversation(conv);
    setConversationMessages(conv.messages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at)));
    setDialogOpen(true);
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('ar', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'أمس';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('ar', { weekday: 'long' });
    } else {
      return date.toLocaleDateString('ar');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
            <MessageCircle className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-stone-800">رسائل الواتساب</h1>
            <p className="text-stone-500 text-sm">{conversations.length} محادثة</p>
          </div>
        </div>
        <Button onClick={fetchData} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          تحديث
        </Button>
        <Button 
          onClick={() => setTestDialogOpen(true)} 
          className="gap-2 bg-purple-600 hover:bg-purple-700"
        >
          <Brain className="w-4 h-4" />
          اختبار تحليل رسالة
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="البحث في المحادثات..."
          className="pr-10"
        />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4 text-center">
            <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-80" />
            <p className="text-3xl font-bold">{messages.length}</p>
            <p className="text-sm opacity-90">إجمالي الرسائل</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4 text-center">
            <User className="w-8 h-8 mx-auto mb-2 opacity-80" />
            <p className="text-3xl font-bold">{conversations.length}</p>
            <p className="text-sm opacity-90">المحادثات</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
          <CardContent className="p-4 text-center">
            <CheckCheck className="w-8 h-8 mx-auto mb-2 opacity-80" />
            <p className="text-3xl font-bold">{conversations.filter(c => c.customer).length}</p>
            <p className="text-sm opacity-90">زبائن معروفين</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4 text-center">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-80" />
            <p className="text-3xl font-bold">{conversations.filter(c => !c.customer).length}</p>
            <p className="text-sm opacity-90">أرقام غير مسجلة</p>
          </CardContent>
        </Card>
      </div>

      {/* Conversations List */}
      {filteredConversations.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-stone-500">
            <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg">لا توجد رسائل واتساب</p>
            <p className="text-sm mt-2">ستظهر هنا الرسائل الواردة من Webhook الواتساب</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filteredConversations.map((conv) => (
            <Card 
              key={conv.phone}
              className="cursor-pointer hover:shadow-md transition-shadow border-s-4 border-s-green-500"
              onClick={() => openConversation(conv)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  {/* Avatar */}
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    conv.customer ? 'bg-green-100' : 'bg-amber-100'
                  }`}>
                    {conv.customer ? (
                      <Store className="w-6 h-6 text-green-600" />
                    ) : (
                      <User className="w-6 h-6 text-amber-600" />
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-bold text-stone-800 truncate">
                        {conv.customer?.name || 'رقم غير مسجل'}
                      </h3>
                      <span className="text-xs text-stone-500 whitespace-nowrap">
                        {formatTime(conv.lastMessage?.created_at)}
                      </span>
                    </div>
                    
                    {conv.customer?.shop_name && (
                      <p className="text-sm text-stone-500 truncate">{conv.customer.shop_name}</p>
                    )}
                    
                    <div className="flex items-center gap-2 mt-1">
                      <Phone className="w-3 h-3 text-stone-400" />
                      <span className="text-xs text-stone-400" dir="ltr">{conv.phone}</span>
                    </div>
                    
                    <p className="text-sm text-stone-600 truncate mt-1">
                      {conv.lastMessage?.message_text}
                    </p>
                  </div>
                  
                  {/* Badge */}
                  <div className="flex flex-col items-end gap-2">
                    <Badge variant={conv.customer ? 'default' : 'secondary'} className={
                      conv.customer ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                    }>
                      {conv.customer ? 'زبون' : 'غير مسجل'}
                    </Badge>
                    <span className="text-xs text-stone-400">{conv.messages.length} رسالة</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Conversation Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                selectedConversation?.customer ? 'bg-green-100' : 'bg-amber-100'
              }`}>
                {selectedConversation?.customer ? (
                  <Store className="w-5 h-5 text-green-600" />
                ) : (
                  <User className="w-5 h-5 text-amber-600" />
                )}
              </div>
              <div>
                <p className="font-bold">{selectedConversation?.customer?.name || 'رقم غير مسجل'}</p>
                <p className="text-sm text-stone-500 font-normal" dir="ltr">{selectedConversation?.phone}</p>
              </div>
              {selectedConversation?.customer && (
                <a 
                  href={`/customers`}
                  className="ms-auto text-sm text-blue-600 hover:underline flex items-center gap-1"
                >
                  <ExternalLink className="w-4 h-4" />
                  عرض الزبون
                </a>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {/* Messages */}
          <div className="flex-1 overflow-y-auto py-4 space-y-3 min-h-[300px]">
            {conversationMessages.map((msg, index) => (
              <div key={msg.id || index} className="flex justify-start">
                <div className="max-w-[80%] bg-white border rounded-lg p-3 shadow-sm">
                  <p className="text-stone-800">{msg.message_text}</p>
                  <div className="flex items-center justify-end gap-2 mt-2">
                    <span className="text-xs text-stone-400">
                      {new Date(msg.created_at).toLocaleString('ar')}
                    </span>
                    <CheckCheck className="w-4 h-4 text-green-500" />
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Info Footer */}
          {!selectedConversation?.customer && (
            <div className="border-t pt-4 bg-amber-50 -mx-6 px-6 -mb-6 pb-4 rounded-b-lg">
              <p className="text-sm text-amber-700 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                هذا الرقم غير مرتبط بزبون. أضف رقم الواتساب في بيانات الزبون لربطه تلقائياً.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Test Message Dialog */}
      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Brain className="w-6 h-6 text-purple-600" />
              اختبار تحليل رسالة WhatsApp
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Input Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">رقم هاتف الزبون</label>
                <div className="relative">
                  <Phone className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    placeholder="مثال: +49123456789"
                    className="pr-10"
                    dir="ltr"
                  />
                </div>
                <p className="text-xs text-stone-500">أدخل رقم WhatsApp المحفوظ في بيانات الزبون</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">الرسالة</label>
                <Textarea
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  placeholder="مثال: أريد 5 صناديق خبز كبير و 3 صناديق سميط"
                  rows={3}
                />
              </div>
            </div>

            <Button 
              onClick={handleTestMessage} 
              disabled={testLoading || !testPhone || !testMessage}
              className="w-full gap-2 bg-purple-600 hover:bg-purple-700"
            >
              {testLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              تحليل الرسالة بالذكاء الاصطناعي
            </Button>

            {/* Results Section */}
            {testResult && (
              <div className="space-y-4 border-t pt-4">
                {/* Customer Info */}
                <Card className={testResult.customer ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <User className={`w-5 h-5 ${testResult.customer ? 'text-green-600' : 'text-red-600'}`} />
                      <span className="font-bold">{testResult.customer ? 'تم التعرف على الزبون' : 'زبون غير معروف'}</span>
                    </div>
                    {testResult.customer ? (
                      <div className="text-sm space-y-1">
                        <p><strong>الاسم:</strong> {testResult.customer.name}</p>
                        <p><strong>المحل:</strong> {testResult.customer.shop_name || '-'}</p>
                        <p><strong>الهاتف:</strong> {testResult.customer.phone}</p>
                      </div>
                    ) : (
                      <p className="text-sm text-red-600">
                        لم يتم العثور على زبون برقم {testPhone}. 
                        تأكد من إضافة رقم WhatsApp في بيانات الزبون.
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Parsed Order */}
                {testResult.parsed_order && (
                  <Card className="border-purple-200 bg-purple-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <ShoppingCart className="w-5 h-5 text-purple-600" />
                        الطلب المستخرج
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {testResult.parsed_order.is_valid ? (
                        <>
                          <div className="space-y-2">
                            {testResult.parsed_order.items?.map((item, idx) => (
                              <div key={idx} className="flex justify-between items-center bg-white p-2 rounded border">
                                <span className="font-medium">{item.product_name || item.product_id}</span>
                                <Badge variant="secondary">{item.quantity} {item.unit || 'صندوق'}</Badge>
                              </div>
                            ))}
                          </div>
                          
                          {testResult.customer && (
                            <Button 
                              onClick={handleCreateOrderFromTest}
                              disabled={creatingOrder}
                              className="w-full gap-2 bg-green-600 hover:bg-green-700"
                            >
                              {creatingOrder ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <ShoppingCart className="w-4 h-4" />
                              )}
                              إنشاء الطلب الآن
                            </Button>
                          )}
                        </>
                      ) : (
                        <div className="text-center py-4">
                          <AlertCircle className="w-10 h-10 mx-auto mb-2 text-amber-500" />
                          <p className="text-amber-700">{testResult.parsed_order.clarification_message || 'لم يتم التعرف على الطلب'}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Raw AI Response */}
                <details className="text-sm">
                  <summary className="cursor-pointer text-stone-500 hover:text-stone-700">عرض استجابة AI الخام</summary>
                  <pre className="mt-2 p-2 bg-stone-100 rounded text-xs overflow-auto max-h-40 dir-ltr">
                    {JSON.stringify(testResult, null, 2)}
                  </pre>
                </details>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WhatsAppMessagesPage;
